import React, { useState } from 'react';
import { 
  AlertTriangle, 
  ExternalLink, 
  Sparkles, 
  ChevronDown,
  ChevronUp,
  CheckCircle,
  AlertCircle,
  XCircle,
  Info
} from 'lucide-react';
import '../styles/NatureAlerts.css';

/**
 * NatureAlerts v2 - Briefing Intel·ligent
 * 
 * Mostra un briefing natural generat per IA amb:
 * - Text conversacional (no llista)
 * - Nivell de risc visual
 * - 2-3 enllaços destacats (no tots)
 * - Opció d'expandir totes les alertes
 */
const NatureAlerts = ({ alerts, aiSynthesis }) => {
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  
  // Si no hi ha contingut, no mostrar res
  const hasBriefing = aiSynthesis?.briefing || aiSynthesis?.has_content;
  const hasAlerts = alerts && alerts.length > 0;
  
  if (!hasBriefing && !hasAlerts) return null;

  // === HELPERS DE RISC ===
  const getRiskConfig = (level) => {
    const configs = {
      'baix': {
        icon: <CheckCircle size={20} />,
        label: 'Zona Tranquil·la',
        className: 'risk-low',
        bgColor: 'rgba(34, 197, 94, 0.1)',
        borderColor: '#22c55e'
      },
      'moderat': {
        icon: <AlertCircle size={20} />,
        label: 'Precaució',
        className: 'risk-moderate',
        bgColor: 'rgba(245, 158, 11, 0.1)',
        borderColor: '#f59e0b'
      },
      'alt': {
        icon: <AlertTriangle size={20} />,
        label: 'Atenció',
        className: 'risk-high',
        bgColor: 'rgba(249, 115, 22, 0.1)',
        borderColor: '#f97316'
      },
      'critic': {
        icon: <XCircle size={20} />,
        label: 'Alerta Crítica',
        className: 'risk-critical',
        bgColor: 'rgba(239, 68, 68, 0.1)',
        borderColor: '#ef4444'
      },
      'crític': {
        icon: <XCircle size={20} />,
        label: 'Alerta Crítica',
        className: 'risk-critical',
        bgColor: 'rgba(239, 68, 68, 0.1)',
        borderColor: '#ef4444'
      }
    };
    return configs[level?.toLowerCase()] || {
      icon: <Info size={20} />,
      label: 'Info',
      className: 'risk-unknown',
      bgColor: 'rgba(107, 114, 128, 0.1)',
      borderColor: '#6b7280'
    };
  };

  const getConcernIcon = (concern) => {
    const icons = {
      'emergencia': '🔥',
      'fauna': '🐗',
      'accessos': '🚧',
      'meteo': '⛈️',
      'events': '🚴',
      'mediambiental': '🌿',
      'cap': '✨'
    };
    return icons[concern] || '📰';
  };

  const riskConfig = getRiskConfig(aiSynthesis?.risk_level);
  const highlightedLinks = aiSynthesis?.highlighted_links || [];

  return (
    <section className="nature-alerts-section fade-in">
      <div className="alerts-container">
        
        {/* Header simplificat */}
        <div className="alerts-header">
          <div className="alerts-header-left">
            <span className="concern-icon">{getConcernIcon(aiSynthesis?.main_concern)}</span>
            <div>
              <h3>Context de la Zona</h3>
              <p className="alerts-subtitle">
                {aiSynthesis?.ai_generated ? (
                  <><Sparkles size={14} className="inline-icon" /> Anàlisi intel·ligent de notícies recents</>
                ) : (
                  'Informació de la zona'
                )}
              </p>
            </div>
          </div>
          
          {/* Badge de risc */}
          {aiSynthesis?.risk_level && (
            <div 
              className={`risk-badge-new ${riskConfig.className}`}
              style={{ 
                backgroundColor: riskConfig.bgColor,
                borderColor: riskConfig.borderColor
              }}
            >
              {riskConfig.icon}
              <span>{riskConfig.label}</span>
            </div>
          )}
        </div>

        {/* Briefing principal */}
        {aiSynthesis?.briefing && (
          <div 
            className="briefing-card"
            style={{ 
              borderLeftColor: riskConfig.borderColor,
              backgroundColor: riskConfig.bgColor
            }}
          >
            <p className="briefing-text">
              {aiSynthesis.briefing}
            </p>
          </div>
        )}

        {/* Enllaços destacats */}
        {highlightedLinks.length > 0 && (
          <div className="highlighted-links">
            <p className="highlighted-links-title">📎 Fonts consultades:</p>
            <div className="highlighted-links-list">
              {highlightedLinks.map((link, idx) => (
                <a 
                  key={idx}
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="highlighted-link"
                >
                  <span className="link-icon">{link.icon || '📰'}</span>
                  <span className="link-title">{link.title}</span>
                  {link.source && <span className="link-source">({link.source})</span>}
                  <ExternalLink size={12} className="link-external" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Botó per veure totes les alertes (opcional) */}
        {hasAlerts && alerts.length > highlightedLinks.length && (
          <button 
            className="show-all-btn"
            onClick={() => setShowAllAlerts(!showAllAlerts)}
          >
            <span>
              {showAllAlerts ? 'Amagar' : 'Veure'} totes les alertes ({alerts.length})
            </span>
            {showAllAlerts ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        )}

        {/* Llista completa d'alertes (col·lapsada per defecte) */}
        {showAllAlerts && hasAlerts && (
          <div className="all-alerts-grid">
            {alerts.map((alert, index) => (
              <div key={index} className={`alert-card-mini category-${alert.category}`}>
                <div className="alert-card-mini-header">
                  <span className="alert-icon-mini">{alert.icon}</span>
                  <span className="alert-category-mini">{alert.category}</span>
                  <span className="alert-origin-mini">
                    {alert.origin === 'google' ? '📰' : '🔵'}
                  </span>
                </div>
                <p className="alert-title-mini">{alert.title}</p>
                {alert.link && (
                  <a 
                    href={alert.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="alert-link-mini"
                  >
                    Llegir <ExternalLink size={10} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer amb fonts */}
        {aiSynthesis?.sources && (
          <div className="alerts-footer">
            <span className="source-info">
              📊 {aiSynthesis.total || alerts?.length || 0} notícies analitzades
              {aiSynthesis.sources.google_news > 0 && ` · Google News: ${aiSynthesis.sources.google_news}`}
              {aiSynthesis.sources.gnews_api > 0 && ` · GNews: ${aiSynthesis.sources.gnews_api}`}
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default NatureAlerts;